import { useId, useMemo } from 'react';
import { useSelectCore } from '../hooks/useCoreSelect';
import { useSelectFloating } from '../hooks/useSelectFloating';
import { Ctx, SelectCtx } from '../hooks/useCoreContext';
import { Option } from '@/legacy/types';
import { adaptSelectProps } from '../utils';
import { ALL_LIT } from '@/legacy/utils/seleactAll';

export type RootProps = {
	options: Option[];
	value: string | number | (string | number)[];
	onChange: (v: any) => void;
	/** Called every time an option is selected, even if value doesn't change */
	onSelect?: (v: string | number) => void;
	children: React.ReactNode;
	multiple?: boolean;
	selectAllRow?: boolean;
	creatable?: boolean;
	onCreateOption?: (label: string) => Promise<Option | null>;
	dontOpenDropdownIfNoOptions?: boolean;
	floatingUiProps?: {
		strategy?: 'absolute' | 'fixed';
		autoplacement?: boolean;
		ignoreSize?: boolean;
	};
	pillsVariant?: boolean;
	useAllSentinel?: boolean;
	reorderSelectedToTop?: boolean;
};

const Root = ({
	options,
	value,
	onChange,
	onSelect,
	children,
	multiple,
	selectAllRow,
	creatable = false,
	onCreateOption,
	dontOpenDropdownIfNoOptions,
	floatingUiProps,
	pillsVariant = false,
	useAllSentinel = false,
	reorderSelectedToTop = true,
}: RootProps) => {
	const baseId = useId();

	const ids = useMemo(
		() => ({
			base: baseId,
			label: `${baseId}-label`,
			input: `${baseId}-input`,
			listbox: `${baseId}-listbox`,
			trigger: `${baseId}-trigger`,
			error: `${baseId}-error`,
			option: (i: number) => `${baseId}-opt-${i}`,
		}),
		[baseId],
	);

	const { coreValue, coreOnChange, isAll, setAll, clearAll } = useMemo(
		() =>
			adaptSelectProps(
				value as (string | number)[] | typeof ALL_LIT,
				onChange as any,
				options?.length,
				useAllSentinel,
			),
		[value, onChange, options?.length, useAllSentinel],
	);

	const core = useSelectCore({
		options,
		value: multiple ? coreValue : value,
		onChange: multiple ? coreOnChange : onChange,
		onSelect,
		multiple,
		selectAllRow,
		creatable,
		onCreateOption,
		dontOpenDropdownIfNoOptions,
		reorderSelectedToTop,
	});

	const floating = useSelectFloating({
		open: core.open,
		onOpenChange: core.setOpen,
		highlightedIndex: core.highlightedIndex,
		setHighlightedIndex: core.setHighlightedIndex,
		interactionModeRef: core.interactionModeRef,
		options,
		textInputValue: core.inputValue,
		dontOpenDropdownIfNoOptions,
		...floatingUiProps,
	});

	const isAllActive = isAll;

	const toggleItem = (val: string | number) => {
		if (isAllActive && useAllSentinel) {
			coreOnChange([val]);
			return;
		}
		core.toggle(val);
	};

	const getItemChecked = (val: string | number) => !isAllActive && core.isSelected(val);

	const toggleSelectAllGlobal = () => {
		if (useAllSentinel) {
			isAllActive ? clearAll() : setAll();
		} else {
			core.toggleAllVisible();
		}
	};

	const toggleSelectAllVisible = () => {
		if (!isAllActive) core.toggleAllVisible();
	};

	const ctx = useMemo<Ctx>(
		() => ({
			ids,
			...core,
			toggleItem,
			getItemChecked,
			toggleSelectAllGlobal,
			toggleSelectAllVisible,
			listRef: floating.listRef,
			refs: floating.refs,
			floatingStyles: floating.floatingStyles,
			getFloatingProps: floating.getFloatingProps,
			getReferenceProps: floating.getReferenceProps,
			getItemProps: floating.getItemProps,
			dontOpenDropdownIfNoOptions,
			context: floating.context,
			fieldRef: floating.fieldRef,
			pillsVariant,
			isAllActive,
			useAllSentinel,
		}),
		[
			ids,
			core,
			isAllActive,
			floating,
			toggleItem,
			getItemChecked,
			toggleSelectAllGlobal,
			toggleSelectAllVisible,
			dontOpenDropdownIfNoOptions,
			pillsVariant,
		],
	);

	return <SelectCtx.Provider value={ctx}>{children}</SelectCtx.Provider>;
};

export default Root;
